/**
 * 趋势表格子组件
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { useIntl } from 'umi';
import type { ITrendTableProps } from './types';

/**
 * 将 xAxis + 多组 series 转为行数据（时间倒序）
 */
export const buildTableRows = (xAxis: string[], seriesList: any[]) =>
  xAxis
    .map((time, index) => {
      const row: any = { time };
      seriesList.forEach((series: any) => {
        series?.forEach((s: any) => {
          row[s.name] = s.data[index];
        });
      });
      return row;
    })
    .reverse();

const TrendTable: React.FC<ITrendTableProps> = ({
  data,
  metricLabel,
  crossAnalysisLabel,
  crossAnalysisData,
  height,
  filter,
}) => {
  const { formatMessage } = useIntl();

  // ==================== 表格数据 ====================
  const tableData = useMemo(() => {
    if (!data?.xAxis || !data?.series) return [];

    return buildTableRows(
      data.xAxis,
      [data.series, crossAnalysisData?.series].filter(Boolean),
    );
  }, [data, crossAnalysisData]);

  // ==================== 表格列定义 ====================
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'time',
        header: formatMessage({ id: 'Date' }),
        render: (record: any) =>
          DBO_EC.toolTime(record.time, filter?.date_type || 'daily'),
        width: filter?.date_type === 'weekly' ? 220 : 160,
      },
    ];

    // 添加数据列
    if (data?.legend) {
      data.legend.forEach((name) => {
        cols.push({
          key: name,
          align: 'right',
          header: metricLabel || formatMessage({ id: name }),
          render: (record: any) => DBO_V.reviews(record[name] ?? 0),
        });
      });
    }

    // 交叉分析数据列
    if (crossAnalysisData?.series?.length) {
      crossAnalysisData.series.forEach((s) => {
        cols.push({
          key: s.name,
          header: crossAnalysisLabel || s.name,
          align: 'right',
          render: (record: any) => DBO_V.reviews(record[s.name] ?? 0),
        });
      });
    }

    return cols;
  }, [
    data,
    formatMessage,
    metricLabel,
    crossAnalysisData,
    crossAnalysisLabel,
    filter,
  ]);

  // ==================== 渲染 ====================
  if (!data?.series?.length || data?.isEmpty) {
    return (
      <div className="dbo-trend-table__empty">
        {formatMessage({ id: 'no_data' })}
      </div>
    );
  }

  return (
    <div className="dbo-trend-table">
      <TableCommonHook
        columns={columns}
        records={tableData}
        hasPageable={false}
        maxHeight={height - 49.5}
        minWidth={'auto'}
      />
    </div>
  );
};

export default React.memo(TrendTable);
